/**
 * PropertyBadge Component
 * Displays property values as colored badges on cards
 * Notion-inspired design
 */

import React from 'react';
import { Box, Text, Group, ThemeIcon, Tooltip } from '@mantine/core';
import { 
  IconCheck, IconX, IconLink, IconCalendar, 
  IconFile, IconHash, IconAt, IconPhone
} from '@tabler/icons-react';
import { 
  PropertyType, 
  PropertyColor, 
  PROPERTY_COLORS,
  PropertyValue
} from '../../types/database/property';
import { getPropertyColor } from '../../utils/propertyUtils';

interface PropertyBadgeProps {
  propertyId: string;
  propertyName: string;
  propertyType: PropertyType;
  value: unknown;
  color?: PropertyColor;
  compact?: boolean;
  onClick?: () => void;
}

export const PropertyBadge: React.FC<PropertyBadgeProps> = ({
  propertyId,
  propertyName,
  propertyType,
  value,
  color = 'gray',
  compact = false,
  onClick,
}) => {
  const colors = PROPERTY_COLORS[color];
  
  // Don't render if no value (unless checkbox)
  if (value === null || value === undefined || value === '') {
    return null;
  }
  
  const renderValue = (): React.ReactNode => {
    switch (propertyType) {
      case 'checkbox':
        return value ? (
          <IconCheck size={12} style={{ color: colors.text }} />
        ) : (
          <IconX size={12} style={{ color: '#999' }} />
        );
      
      case 'select':
      case 'multi_select':
        if (Array.isArray(value)) {
          return value.map((v, i) => {
            const optionColor = getPropertyColor(v.color);
            return (
              <Box
                key={i}
                style={{
                  backgroundColor: PROPERTY_COLORS[optionColor].bg,
                  color: PROPERTY_COLORS[optionColor].text,
                }}
                className="property-badge-item"
              >
                {v.name}
              </Box>
            );
          });
        }
        return (
          <Box
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
            }}
            className="property-badge-item"
          >
            {String(value)}
          </Box>
        );
      
      case 'date':
        const date = new Date(value as string);
        return (
          <Text size="xs" c={colors.text}>
            {date.toLocaleDateString()}
          </Text>
        );
      
      case 'number':
        const num = Number(value);
        return (
          <Text size="xs" c={colors.text} fw={500}>
            {isNaN(num) ? value : num.toLocaleString()}
          </Text>
        );
      
      case 'url':
        return (
          <Group gap={4}>
            <IconLink size={12} style={{ color: colors.text }} />
            <Text size="xs" c={colors.text} className="truncate" style={{ maxWidth: 100 }}>
              {String(value)}
            </Text>
          </Group>
        );
      
      case 'files':
        const files = Array.isArray(value) ? value : [value];
        return (
          <Group gap={4}>
            <IconFile size={12} style={{ color: colors.text }} />
            <Text size="xs" c={colors.text}>
              {files.length} file{files.length !== 1 ? 's' : ''}
            </Text>
          </Group>
        );
      
      case 'status':
        return (
          <Box
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
            }}
            className="property-badge-item status-badge"
          >
            {String(value)}
          </Box>
        );
      
      default:
        return (
          <Text size="xs" c={colors.text} className="truncate" style={{ maxWidth: 120 }}>
            {String(value)}
          </Text>
        );
    }
  };
  
  if (compact) {
    return (
      <Tooltip label={`${propertyName}: ${value}`} position="top">
        <Box
          onClick={onClick}
          style={{
            backgroundColor: colors.bg,
            color: colors.text,
            cursor: onClick ? 'pointer' : 'default',
          }}
          className="property-badge-compact"
        >
          {renderValue()}
        </Box>
      </Tooltip>
    );
  }
  
  return (
    <Tooltip label={`${propertyName}: ${value}`} position="top">
      <Box
        onClick={onClick}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          cursor: onClick ? 'pointer' : 'default',
        }}
        className="property-badge"
      >
        <Text size="xs" fw={500} className="truncate" style={{ maxWidth: 150 }}>
          {propertyName && renderValue()}
        </Text>
      </Box>
    </Tooltip>
  );
};

export default PropertyBadge;
